import { API_BASE_URL } from '../config';

const baseUrl= API_BASE_URL;

export const fetchDashboardData = async (token) => {
  const url = `${baseUrl}users/dashboard`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch dashboard data: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    throw error;
  }
};

// src/services/messages.services.js

export const getMessages = async (token) => {
  const url = `${baseUrl}messages`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw error;
  }
};

// userServices.js

export async function updateUserData(userData, token, language = "fr") {
  const apiUrl = `${baseUrl}users`;

  try {
    const response = await fetch(apiUrl, {
      method: "PUT",
      headers: {
        "Accept-Language": language,
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const error = new Error(
        errorData.message || "Failed to update user data"
      );
      error.errors = errorData.errors; // Attach the errors array
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating user data:", error);
    throw error;
  }
}

// Add this function to your existing userServices.js file

export async function updateBankDetails(bankData, token, language = "fr") {
  const apiUrl = `${API_BASE_URL}users/bank`;

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Accept-Language": language,
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(bankData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const error = new Error(
        errorData.message || "Failed to update bank details"
      );
      error.errors = errorData.errors;
      throw error;
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating bank details:", error);
    throw error;
  }
}

export async function fetchCurrentBookings(accessToken, sortBy = "date", order = "desc") {
  // Build URL with query parameters
  const queryParams = new URLSearchParams({
    order_by: sortBy,
    order: order,
    page: 1,
    perPage: 40
  });
  
  const apiUrl = `${baseUrl}users/bookings/current?${queryParams.toString()}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching bookings: ${response.statusText}`);
    }

    const data = await response.json();
    return data; // Return the parsed JSON data
  } catch (error) {
    console.error("Error in fetchCurrentBookings:", error.message || error);
    throw error; // Re-throw the error for further handling
  }
}

export const getBookingsHistory = async (
  accessToken,
  {
    page = 1,
    perPage = 16,
    fdate = "all",
    order = "desc",
    orderBy = "date",
  } = {}
) => {
  const queryParams = new URLSearchParams({
    page,
    perPage,
    fdate,
    order,
    order_by: orderBy,
  });

  const response = await fetch(`${baseUrl}users/bookings/past?${queryParams}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch bookings history");
  }

  return await response.json();
};

export const fetchBookingInvoices = async (authToken) => {
  const url = `${baseUrl}bookings_invoices`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data; // Return the parsed JSON data
  } catch (error) {
    console.error("Failed to fetch booking invoices:", error.message);
    throw error; // Re-throw the error to be handled by the calling function
  }
};

export const fetchTrainingInvoices = async (authToken) => {
  const url = `${baseUrl}training_invoices`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data; // Return the parsed JSON data
  } catch (error) {
    console.error("Failed to fetch booking invoices:", error.message);
    throw error; // Re-throw the error to be handled by the calling function
  }
};
// services/commissionInvoices.services.js

export const fetchCommissionInvoices = async (
  accessToken,
  order = "desc",
  orderBy = "date"
) => {
  const url = new URL(`${baseUrl}invoices`);

  // Append query parameters to the URL
  url.searchParams.append("order", order);
  url.searchParams.append("order_by", orderBy);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`, // Bearer token for authorization
        Accept: "application/json",
      },
    });

    // Check if the response is successful (status code 200)
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    // Parse and return the response JSON data
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching invoices:", error);
    throw error; // Rethrow the error for handling in the calling component
  }
};

// spaceService.js

export const fetchProviderSpaces = async (
  accessToken,
  currentPage,
  perPage = 8
) => {
  try {
    const response = await fetch(
      `${baseUrl}users/places?page=${currentPage}&perPage=${perPage}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch provider spaces");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching provider spaces:", error);
    throw error;
  }
};

export const fetchAllProviderSpaces = async (accessToken) => {
  try {
    const response = await fetch(
      `${baseUrl}users/places?all=true`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch all provider spaces");
    }

    const data = await response.json();
    return data.data ? data : { data }; // Ensure consistent response format
  } catch (error) {
    console.error("Error fetching all provider spaces:", error);
    throw error;
  }
};
